import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";

import { brushSizeSliderActiveAtom } from "@design-lab/widgets/artboard/model/state";

type RefineBrushSizeSliderProps = {
  brushSize: number;
  onBrushSizeChange: (size: number) => void;
};

export function RefineBrushSizeSlider({
  brushSize,
  onBrushSizeChange
}: RefineBrushSizeSliderProps) {
  const setSliderActive = useSetAtom(brushSizeSliderActiveAtom);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setSliderActive(false);
      }
    };

    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchend", handleMouseUp);

    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchend", handleMouseUp);
      setSliderActive(false);
    };
  }, [setSliderActive]);

  const handleMouseEnter = () => {
    setSliderActive(true);
  };

  const handleMouseLeave = () => {
    if (!isDraggingRef.current) {
      setSliderActive(false);
    }
  };

  const handleMouseDown = () => {
    isDraggingRef.current = true;
    setSliderActive(true);
  };

  return (
    <>
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          background: white;
          border-radius: 50%;
          border: 2px solid #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          background: white;
          border-radius: 50%;
          border: 2px solid #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="flex gap-2 rounded-b-lg p-2 px-4">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="refine-erase-brush-size"
              data-testid="refine-erase-brush-size-label"
              className="text-xs font-medium text-gray-700"
            >
              Brush Size
            </label>
            <input
              data-testid="refine-erase-brush-size"
              id="refine-erase-brush-size"
              type="range"
              min={1}
              max={100}
              step={1}
              value={brushSize}
              onChange={e => onBrushSizeChange(Number(e.target.value))}
              onMouseDown={handleMouseDown}
              onTouchStart={handleMouseDown}
              className={`slider h-1 w-60 cursor-pointer appearance-none rounded-full bg-gray-200`}
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((brushSize - 1) / 99) * 100}%, #e5e7eb ${((brushSize - 1) / 99) * 100}%, #e5e7eb 100%)`
              }}
            />
          </div>
          <div className="flex h-8 w-12 items-center justify-center rounded border border-gray-300 bg-gray-50 text-sm font-medium text-gray-800">
            {brushSize}
          </div>
        </div>
      </div>
    </>
  );
}
